'use client';

import * as React from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/components/atoms/primitives/utils';
import { trackNearbyCardViewed, trackNearbyItemClicked } from '@/lib/analytics';
import { GoogleWordmark } from './GoogleWordmark';

/**
 * The three nearby surfaces shown in the event detail right rail. Picks are
 * Places-backed with a curated overlay at walkable distance.
 * `EventSpineConnected` adapts the real `EventNearbyResponse` into these view
 * props (WEB-417); the Connected wrapper below is a thin pass-through.
 *
 * Empty tabs are omitted from the tablist (FRE-1297 / AE2). The template skips
 * mounting the card when every tab is empty (AE3). Editor picks render a
 * LOCI PICK badge; Google attribution mounts only when the active tab holds
 * Places-sourced items (FRE-1298 / R7 / R10). Card view + outbound item clicks
 * are instrumented for the R9 bet review (FRE-1299). Places-sourced rows carry
 * their own provenance tray + marker, and the footer attribution is scoped to
 * those rows rather than blended over the whole list (FRE-1303).
 */

/** Recent `nearby_card_viewed` fires — StrictMode-safe TTL, not session-lifetime. */
const recentNearbyCardViewFires = new Map<string, number>();

/**
 * Window that collapses React 18 StrictMode's sync double-mount into one
 * capture without blocking a genuine remount after SPA navigation (FRE-1299
 * R9 CTR denominator must count real re-views).
 */
const NEARBY_CARD_VIEW_STRICT_DEDUPE_MS = 500;

/** Test-only escape hatch for clearing the view dedupe map between Vitest runs. */
export function __resetNearbyCardAnalyticsForTests(): void {
  recentNearbyCardViewFires.clear();
}
export type NearbyTabId = 'eat' | 'drink' | 'stay';

/** Wire `EventNearbyItem.source` — drives attribution visibility (FRE-1298). */
export type NearbyItemSource = 'places' | 'curated';

const TAB_ORDER: readonly NearbyTabId[] = ['eat', 'drink', 'stay'] as const;

const TAB_LABEL: Readonly<Record<NearbyTabId, string>> = {
  eat: 'Eat',
  drink: 'Drink',
  stay: 'Stay',
};

/**
 * Thumbnail disc colour token per tab. The values map to the per-city tokens
 * emitted by `ThemeProvider` so the disc swaps automatically under
 * `data-theme="dark"` / `data-theme="light"`.
 */
const TAB_THUMB_BG: Readonly<Record<NearbyTabId, string>> = {
  // WEB-459: `--clt-ember-deep` (not `--clt-ember`) so the WHITE disc initials
  // clear WCAG AA — the brand ember is too light for white text (2.7:1). The
  // skyline / navy discs already pass white at ≥5:1.
  eat: 'var(--clt-ember-deep)',
  drink: 'var(--clt-skyline)',
  stay: 'var(--clt-navy)',
};

export interface NearbyItem {
  /**
   * Stable identifier from the `EventNearbyItem` contract. Used as the React
   * list key so rows stay identity-stable across re-renders and tab switches
   * (previously keyed by array index + name, which churned on reorder).
   */
  id: string;
  /** Display name of the place (e.g. "Soul Gastrolounge"). */
  name: string;
  /** Type + price-tier line ("Small plates · $$"). */
  type: string;
  /**
   * Numeric rating (e.g. 4.6). Displayed to one decimal place. Optional —
   * Places-sourced items omit rating (Pro-tier field mask); the row skips the
   * rating segment rather than fabricating a placeholder (FRE-1297).
   */
  rating?: number;
  /**
   * Distance in miles (e.g. 0.4). Optional — omit the segment when the wire
   * payload leaves it unset rather than fabricating `0.0 mi` (FRE-1298 review).
   */
  distanceMiles?: number;
  /**
   * Human-readable hours label ("Open till 2am", "Closes 11pm"). Optional —
   * omitted on Places-sourced items; the row skips the hours segment and
   * open/closed tinting when absent (FRE-1297).
   */
  hoursLabel?: string;
  /** Whether the venue is currently open. Only meaningful when hoursLabel is set. */
  isOpen?: boolean;
  /**
   * Editorial LOCI pick (replaces the retired PARTNER claim — FRE-1298 / R7).
   * Renders the LOCI PICK badge inline with the name.
   */
  isEditorPick: boolean;
  /**
   * Supply provenance. Attribution visibility is derived from the **active**
   * tab's items — a curated-only tab must not claim Google sourcing (R10).
   */
  source?: NearbyItemSource;
  /** Open-in-maps deep link (`mapsUrl` when present, else Maps search fallback). */
  href: string;
}

export interface NearbyRecommendationsCardViewProps {
  tabs: {
    eat: NearbyItem[];
    drink: NearbyItem[];
    stay: NearbyItem[];
  };
  /**
   * Parent event id — required for FRE-1299 analytics (`nearby_card_viewed` /
   * `nearby_item_clicked`). Wired from `EventSpineConnected`.
   */
  eventId: string;
  /**
   * Tab to show on first render when that tab is populated. Falls back to the
   * first populated tab. Defaults to the first populated tab in TAB_ORDER.
   */
  initialTab?: NearbyTabId;
  /** How many items to show in the collapsed (default) state. Default 3. */
  initialItemsVisible?: number;
  /** Cap on the expanded list so the right rail can't run away. Default 12. */
  expandedItemsVisible?: number;
  /**
   * Radius label rendered next to the title. Default `'WALKABLE'` — per-tab
   * radii differ, so a single numeric claim (e.g. ¼ mi) is not honest (FRE-1298).
   */
  radiusLabel?: string;
  /** Additional CSS classes applied to the root container. */
  className?: string;
}

/**
 * Row-level provenance marker rendered on every Google-sourced row (FRE-1303).
 * Exported so the scope caption, the accessible name, and tests all read from
 * one string — the caption points at this literal text.
 */
export const NEARBY_PLACES_SOURCE_LABEL = 'via Google Maps';

/**
 * Visible caption beside the wordmark on a mixed tab. Names *which* rows the
 * logo covers so the mark cannot read as attributing LOCI-authored rows to
 * Google Maps ("Don't misrepresent Google Maps by attributing it with
 * non-Google Maps Platform content" — Places policies, Visual requirements).
 */
export const NEARBY_ATTRIBUTION_SCOPE_CAPTION = `for listings marked "${NEARBY_PLACES_SOURCE_LABEL}"`;

const SCOPED_ATTRIBUTION_ACCESSIBLE_NAME = `Google Maps attribution for listings marked ${NEARBY_PLACES_SOURCE_LABEL}`;

/**
 * Attribution state for the rows currently on screen. Returns null when none
 * of them is Places-sourced (curated-only → no Google claim).
 *
 * **Pass the rendered slice, not the whole tab.** Google's requirement attaches
 * to Content that is *displayed*; a mark covering rows the user cannot see
 * claims sourcing for nothing, and the scope caption would point at a marker
 * that isn't on screen.
 *
 * `'scoped'` replaces the retired `'blended'` kind (FRE-1303). The blended
 * "Google Maps + LOCI" footer named both providers under one mark, which reads
 * as attributing the entire list — including LOCI-authored rows — to Google.
 * The policy's remedy is to *distinguish the content* (see `NearbyItemRow`'s
 * provenance tray) and then scope the mark to the distinguished rows.
 */
export function getNearbyTabAttribution(
  items: readonly NearbyItem[],
): { kind: 'places' | 'scoped'; accessibleName: string } | null {
  const hasPlaces = items.some((item) => item.source === 'places');
  if (!hasPlaces) return null;
  // Any non-Places item (including omitted `source`) narrows the mark to the
  // marked rows — treating missing provenance as Places-only would be a false
  // sourcing claim when curated/unknown rows are interleaved.
  const hasNonPlaces = items.some((item) => item.source !== 'places');
  if (hasNonPlaces) {
    return {
      kind: 'scoped',
      accessibleName: SCOPED_ATTRIBUTION_ACCESSIBLE_NAME,
    };
  }
  return { kind: 'places', accessibleName: 'Powered by Google' };
}

const TAB_ID_PREFIX = 'nearby-recs-tab';
const PANEL_ID_PREFIX = 'nearby-recs-panel';

/**
 * Derive the rendered tab order from populated tabs only — empty rails are
 * never shown (FRE-1297 / AE2). Preserves the canonical eat → drink → stay
 * order among the tabs that have items.
 */
export function getPopulatedNearbyTabIds(
  tabs: NearbyRecommendationsCardViewProps['tabs'],
): NearbyTabId[] {
  return TAB_ORDER.filter((tabId) => tabs[tabId].length > 0);
}

/**
 * NearbyRecommendationsCardView — "Nearby" card with dynamic Eat / Drink /
 * Stay tabs for the event detail right rail.
 *
 * Implements WAI-ARIA tablist semantics with roving-focus keyboard navigation
 * over the **rendered** tab list only: ArrowLeft / ArrowRight cycle (with
 * wrap-around), Home / End jump to first / last. Each tab acts as a
 * manual-activation tab (focus moves selection), mirroring the SubGenreChips
 * pattern already established in the codebase.
 *
 * Empty tabs are omitted; when every tab is empty the component returns null
 * (the template also skips mounting — defense in depth). Rows omit rating /
 * hours segments when those fields are absent — never a fabricated "0.0" or
 * "Hours TBA".
 */
export function NearbyRecommendationsCardView({
  tabs,
  eventId,
  initialTab,
  initialItemsVisible = 3,
  expandedItemsVisible = 12,
  radiusLabel = 'Walkable',
  className,
}: NearbyRecommendationsCardViewProps): React.ReactElement | null {
  const populatedTabIds = React.useMemo(() => getPopulatedNearbyTabIds(tabs), [tabs]);

  const resolvedInitialTab = React.useMemo((): NearbyTabId | null => {
    if (populatedTabIds.length === 0) return null;
    if (initialTab !== undefined && populatedTabIds.includes(initialTab)) {
      return initialTab;
    }
    return populatedTabIds[0]!;
  }, [initialTab, populatedTabIds]);

  const [activeTab, setActiveTab] = React.useState<NearbyTabId | null>(resolvedInitialTab);
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);

  // Keep activeTab in sync when the populated set changes (e.g. story controls
  // or a parent re-fetch that drops the previously active rail).
  React.useEffect(() => {
    if (resolvedInitialTab === null) {
      setActiveTab(null);
      return;
    }
    setActiveTab((current) => {
      if (current !== null && populatedTabIds.includes(current)) {
        return current;
      }
      return resolvedInitialTab;
    });
  }, [populatedTabIds, resolvedInitialTab]);

  // FRE-1299 / R9: one card-view event per rendered mount. Skips when every
  // tab is empty (component returns null). A short TTL Map collapses React 18
  // StrictMode's sync double-mount without under-counting genuine SPA remounts
  // of the same event (clicks÷views is the bet metric). A per-mount ref
  // prevents re-firing when `populatedTabIds.length` changes mid-mount.
  // Align with the render-null check (`activeTab === null`) so we never fire
  // while the card still returns null (empty → populated prop transition).
  const isCardRendered = populatedTabIds.length > 0 && activeTab !== null;
  const viewedEventIdThisMountRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!isCardRendered) return;
    if (viewedEventIdThisMountRef.current === eventId) return;

    const now = Date.now();
    // Bound the Map across long SPA sessions — drop expired StrictMode entries.
    for (const [id, firedAt] of recentNearbyCardViewFires) {
      if (now - firedAt >= NEARBY_CARD_VIEW_STRICT_DEDUPE_MS) {
        recentNearbyCardViewFires.delete(id);
      }
    }

    const lastFiredAt = recentNearbyCardViewFires.get(eventId);
    if (lastFiredAt !== undefined && now - lastFiredAt < NEARBY_CARD_VIEW_STRICT_DEDUPE_MS) {
      // StrictMode remount within TTL — count as already viewed for this mount.
      viewedEventIdThisMountRef.current = eventId;
      return;
    }

    viewedEventIdThisMountRef.current = eventId;
    recentNearbyCardViewFires.set(eventId, now);
    trackNearbyCardViewed({ eventId });
  }, [eventId, isCardRendered]);

  const tabRefs = React.useRef<Partial<Record<NearbyTabId, HTMLButtonElement | null>>>({});

  const activeIndex = activeTab === null ? -1 : populatedTabIds.indexOf(activeTab);

  const selectTabByIndex = React.useCallback(
    (index: number): void => {
      if (populatedTabIds.length === 0) return;
      const wrapped =
        ((index % populatedTabIds.length) + populatedTabIds.length) % populatedTabIds.length;
      const nextTab = populatedTabIds[wrapped]!;
      setActiveTab(nextTab);
      setIsExpanded(false);
      // Follow-focus to match WAI-ARIA roving tablist pattern.
      tabRefs.current[nextTab]?.focus();
    },
    [populatedTabIds],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (populatedTabIds.length === 0 || activeIndex < 0) return;
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          selectTabByIndex(activeIndex + 1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          selectTabByIndex(activeIndex - 1);
          break;
        case 'Home':
          event.preventDefault();
          selectTabByIndex(0);
          break;
        case 'End':
          event.preventDefault();
          selectTabByIndex(populatedTabIds.length - 1);
          break;
        default:
          break;
      }
    },
    [activeIndex, populatedTabIds.length, selectTabByIndex],
  );

  if (populatedTabIds.length === 0 || activeTab === null) {
    return null;
  }

  // Normalize visibility bounds so the toggle can't invert when callers pass
  // an `initialItemsVisible` that exceeds `expandedItemsVisible` — otherwise
  // collapsed could show more items than expanded.
  const normalizedExpandedItemsVisible = Math.max(0, expandedItemsVisible);
  const normalizedInitialItemsVisible = Math.min(
    Math.max(0, initialItemsVisible),
    normalizedExpandedItemsVisible,
  );

  const activeItems = tabs[activeTab];
  const visibleCount = isExpanded
    ? Math.min(activeItems.length, normalizedExpandedItemsVisible)
    : Math.min(activeItems.length, normalizedInitialItemsVisible);
  const visibleItems = activeItems.slice(0, visibleCount);
  const cappedTotal = Math.min(activeItems.length, normalizedExpandedItemsVisible);
  const hiddenCount = cappedTotal - normalizedInitialItemsVisible;
  const showExpandToggle = hiddenCount > 0;
  // Derived from the **rendered** slice, not the whole tab. The backend merges
  // curated rows ahead of the Places fill, so a tab can easily show three
  // all-curated rows collapsed while its Places rows wait behind the expander
  // — and attributing Google under three LOCI-authored rows, with no "via
  // Google Maps" marker anywhere on screen for the caption to point at, is the
  // exact misattribution FRE-1303 exists to remove. Recomputes on tab switch
  // and on expand/collapse; still returns null for a curated-only tab
  // (FRE-1298 / R10 preserved).
  const attribution = getNearbyTabAttribution(visibleItems);

  const activePanelId = `${PANEL_ID_PREFIX}-${activeTab}`;

  return (
    <section
      data-testid="nearby-recs"
      className={cn(
        'bg-ds-surface',
        'border border-ds-border',
        'rounded-ds-md',
        'p-4',
        'text-fg-2',
        className,
      )}
      aria-label="Nearby recommendations"
    >
      <div className="flex items-center justify-between gap-2">
        <h3
          className={cn(
            'text-small',
            'font-bold',
            'tracking-[0.02em]',
            'text-fg-1',
            'font-display',
          )}
        >
          Nearby{' '}
          <small className="text-micro font-medium tracking-caps text-fg-3">· Before / after</small>
        </h3>
        <span
          data-testid="nearby-recs-radius"
          className="text-micro tracking-[0.1em] text-fg-3 font-mono"
        >
          {radiusLabel}
        </span>
      </div>

      <div
        role="tablist"
        aria-label="Nearby category"
        aria-orientation="horizontal"
        onKeyDown={handleKeyDown}
        className="mt-3 flex items-center gap-1 border-b border-ds-border"
      >
        {populatedTabIds.map((tabId) => {
          const isActive = tabId === activeTab;
          const items = tabs[tabId];
          return (
            <button
              key={tabId}
              type="button"
              role="tab"
              id={`${TAB_ID_PREFIX}-${tabId}`}
              aria-selected={isActive}
              aria-controls={`${PANEL_ID_PREFIX}-${tabId}`}
              tabIndex={isActive ? 0 : -1}
              data-testid={`${TAB_ID_PREFIX}-${tabId}`}
              ref={(el) => {
                tabRefs.current[tabId] = el;
              }}
              onClick={() => {
                if (!isActive) {
                  setActiveTab(tabId);
                  setIsExpanded(false);
                }
              }}
              className={cn(
                'inline-flex min-h-11 desktop:min-h-0 items-center gap-1.5 px-3 py-2',
                'text-micro tracking-caps font-mono',
                'border-b-2 transition-colors motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline-glow focus-visible:ring-offset-1',
                isActive
                  ? 'border-clt-skyline-glow text-clt-skyline-glow-fg'
                  : 'border-transparent text-fg-3 hover:text-fg-2',
              )}
            >
              <span>{TAB_LABEL[tabId]}</span>
              <b
                className={cn(
                  'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5',
                  'text-micro font-bold',
                  isActive ? 'bg-clt-skyline-glow/15' : 'bg-ds-surface-alt',
                )}
              >
                {items.length}
              </b>
            </button>
          );
        })}
      </div>

      {populatedTabIds.map((tabId) => {
        const isActive = tabId === activeTab;
        // Render a panel for every rendered tab so `aria-controls` resolves to
        // a real id in the DOM (WAI-ARIA Tabs pattern). Inactive panels stay
        // hidden via the native `hidden` attribute so they're removed from
        // the a11y tree and out of tab order.
        return (
          <div
            key={tabId}
            role="tabpanel"
            id={`${PANEL_ID_PREFIX}-${tabId}`}
            aria-labelledby={`${TAB_ID_PREFIX}-${tabId}`}
            hidden={!isActive}
            className="mt-3"
          >
            {isActive ? (
              <ul className="flex flex-col gap-3">
                {visibleItems.map((item, index) => (
                  <NearbyItemRow
                    key={item.id}
                    index={index}
                    tab={activeTab}
                    item={item}
                    eventId={eventId}
                  />
                ))}
              </ul>
            ) : null}
          </div>
        );
      })}

      {showExpandToggle && (
        <button
          type="button"
          data-testid="nearby-recs-expand"
          aria-expanded={isExpanded}
          aria-controls={activePanelId}
          onClick={() => setIsExpanded((prev) => !prev)}
          className={cn(
            'mt-3 inline-flex min-h-11 desktop:min-h-0 w-full items-center justify-center gap-2',
            'rounded-ds-sm border border-ds-border bg-transparent',
            'px-3 py-2 text-micro tracking-caps font-mono',
            'text-fg-2 transition-colors motion-reduce:transition-none',
            'hover:bg-ds-surface-alt hover:text-fg-1',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline-glow focus-visible:ring-offset-1',
          )}
        >
          <span>{isExpanded ? 'Show less' : `Show ${hiddenCount} more`}</span>
          <span
            aria-hidden="true"
            className={cn(
              'inline-block transition-transform motion-reduce:transition-none',
              isExpanded ? 'rotate-180' : 'rotate-0',
            )}
          >
            ▾
          </span>
        </button>
      )}

      {attribution ? <GoogleAttribution attribution={attribution} /> : null}
    </section>
  );
}

interface GoogleAttributionProps {
  attribution: NonNullable<ReturnType<typeof getNearbyTabAttribution>>;
}

/**
 * Google Maps attribution for Places content shown without an embedded map
 * (expressly permitted by MST §14.1). Logo height locked to 16–19dp per
 * Google's display guidelines; accessible name is "Powered by Google" when
 * every row is Google-sourced, or the scoped Google Maps wording when curated
 * rows interleave (FRE-1298 / R10, rescoped by FRE-1303).
 */
function GoogleAttribution({ attribution }: GoogleAttributionProps): React.ReactElement {
  return (
    <div
      data-testid="nearby-recs-google-attribution"
      role="img"
      aria-label={attribution.accessibleName}
      className={cn(
        // `gap-2.5` (10dp) keeps Google's clear-space minimum on the wordmark's
        // trailing edge when the scope caption renders beside it.
        'mt-3 flex items-center gap-2.5 border-t border-ds-border pt-3',
        // 10dp horizontal / 5dp bottom clear space around the logo (Google min).
        'px-2.5 pb-1.5',
        // Dark-first: white wordmark; light theme uses the current Google text
        // attribution gray #5E5E5E (supersedes the older Gray 700 #5F6368).
        // WEB-1065: #5E5E5E is Google's mandated attribution grey — a third-party
        // requirement, kept as literal hex on purpose.
        'text-white [[data-theme=light]_&]:text-[#5E5E5E]',
      )}
    >
      {/* Inline SVG — CSP-safe local asset; works in Next + Storybook without staticDirs. */}
      <GoogleWordmark />
      {attribution.kind === 'scoped' ? (
        <span
          data-testid="nearby-recs-attribution-scope"
          // `role="img"` on the container already exposes `accessibleName`,
          // which says the same thing — don't double-announce it.
          aria-hidden="true"
          // Kept untranslated in lockstep with the row marker it quotes.
          translate="no"
          className="text-micro font-normal leading-tight text-fg-3"
        >
          {NEARBY_ATTRIBUTION_SCOPE_CAPTION}
        </span>
      ) : null}
    </div>
  );
}

interface NearbyItemRowProps {
  index: number;
  tab: NearbyTabId;
  item: NearbyItem;
  eventId: string;
}

interface MetaSegment {
  key: string;
  node: React.ReactNode;
}

function NearbyItemRow({ index, tab, item, eventId }: NearbyItemRowProps): React.ReactElement {
  const initials = makeInitials(item.name);
  const hasHours = typeof item.hoursLabel === 'string' && item.hoursLabel.length > 0;
  const openClass = hasHours && item.isOpen === true ? 'text-clt-skyline-glow-fg' : 'text-fg-3';
  const ariaLabel = `Open ${item.name} in maps`;
  // Omitted provenance is not Places — treat as curated for the CTR split and
  // for the provenance tray so Places claims stay honest (matches the same
  // rule `getNearbyTabAttribution` applies to the footer).
  const rowSource: NearbyItemSource = item.source === 'places' ? 'places' : 'curated';
  const isPlacesRow = rowSource === 'places';

  // Build meta segments only for present fields — never fabricate rating "0.0"
  // or hours placeholders when Places omits those Pro-tier fields (FRE-1297).
  const segments: MetaSegment[] = [];
  if (typeof item.rating === 'number') {
    segments.push({
      key: 'rating',
      node: (
        <span className="inline-flex items-center gap-0.5">
          <Star className="inline size-[1em] shrink-0" aria-hidden="true" />
          <span>{item.rating.toFixed(1)}</span>
        </span>
      ),
    });
  }
  segments.push({
    key: 'type',
    node: <span>{item.type}</span>,
  });
  if (typeof item.distanceMiles === 'number') {
    segments.push({
      key: 'distance',
      node: <span>{item.distanceMiles.toFixed(1)} mi</span>,
    });
  }
  if (hasHours) {
    segments.push({
      key: 'hours',
      node: <span className={openClass}>{item.hoursLabel}</span>,
    });
  }
  if (isPlacesRow) {
    // The textual half of the FRE-1303 distinction. It is what makes the cue
    // survive for screen-reader and greyscale users (the tray below is purely
    // visual), and it is the literal string the footer caption points at.
    segments.push({
      key: 'source',
      node: (
        // `translate="no"` on both the marker and the footer caption keeps the
        // pair in lockstep — the caption quotes this literal text, so a browser
        // that machine-translated one but not the other would break the link
        // between the mark and the rows it scopes.
        <span data-testid="nearby-recs-source-marker" translate="no">
          {NEARBY_PLACES_SOURCE_LABEL}
        </span>
      ),
    });
  }

  return (
    <li
      data-testid={`nearby-recs-item-${index}`}
      data-source={rowSource}
      className={cn(
        'flex items-start gap-3',
        isPlacesRow &&
          cn(
            // FRE-1303 — Places policies, "Google Maps attribution requirements
            // → Visual requirements": Google Maps Platform Content must be
            // visually distinguished from other content "by using UI cues such
            // as a border, background color, shadow, or sufficient whitespace."
            // This tray uses three of the four — a 2px left rule, an alternate
            // surface fill, and inset padding. The rule is `--fg-3`, which
            // clears 4.5:1 against the tray fill in both themes, so the cue is
            // a shape boundary rather than a hue difference (never colour
            // alone). `-mx-2` bleeds the tray into the card gutter and `pl-1.5`
            // absorbs the 2px rule, so row content keeps the exact same left
            // edge as curated rows.
            '-mx-2 rounded-ds-sm border-l-2 border-fg-3',
            'bg-ds-surface-alt py-2 pl-1.5 pr-2',
          ),
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-ds-sm',
          'text-micro font-bold tracking-[0.04em] text-white',
        )}
        style={{ backgroundColor: TAB_THUMB_BG[tab] }}
      >
        {initials}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-fg-1">
          <span className="truncate">{item.name}</span>
          {item.isEditorPick ? (
            <span
              data-testid="nearby-recs-loci-pick-badge"
              className={cn(
                // Inline token recipe (same shape as the retired PARTNER badge).
                // packages/ui Badge atoms are event-status oriented — keep the
                // local span rather than force-fitting EventBadge / TagPill.
                'inline-flex items-center rounded-full px-1.5 py-0.5',
                'text-micro font-bold tracking-caps font-mono',
                'bg-clt-ember/15 text-clt-ember-fg',
                'border border-clt-ember/40',
              )}
            >
              LOCI pick
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-fg-3">
          {segments.map((segment, segmentIndex) => (
            <React.Fragment key={segment.key}>
              {segmentIndex > 0 ? <span aria-hidden="true">·</span> : null}
              {segment.node}
            </React.Fragment>
          ))}
        </div>
      </div>
      <a
        href={item.href}
        data-testid={`nearby-recs-link-${index}`}
        aria-label={ariaLabel}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          trackNearbyItemClicked({
            eventId,
            tab,
            source: rowSource,
            isEditorPick: item.isEditorPick,
          });
        }}
        className={cn(
          'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          'text-fg-2 transition-colors motion-reduce:transition-none',
          'hover:bg-ds-surface-alt hover:text-fg-1',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline-glow focus-visible:ring-offset-1',
        )}
      >
        <span aria-hidden="true">↗</span>
      </a>
    </li>
  );
}

/**
 * Build a two-character thumbnail label from a venue name. Splits on
 * whitespace, takes the leading character of the first two tokens, and
 * upper-cases the result. Falls back to the leading two characters of the
 * single token when only one is present.
 */
function makeInitials(name: string): string {
  const tokens = name.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return '?';
  if (tokens.length === 1) {
    const single = tokens[0]!;
    return single.slice(0, 2).toUpperCase();
  }
  const first = tokens[0]!.charAt(0);
  const second = tokens[1]!.charAt(0);
  return `${first}${second}`.toUpperCase();
}

export interface NearbyRecommendationsCardProps extends NearbyRecommendationsCardViewProps {}

/**
 * Connected wrapper — a thin pass-through. The real `EventNearbyResponse` is
 * fetched server-side (`lib/server/fetchEventNearby.ts`) and adapted into the
 * `tabs` view props by `EventSpineConnected` (WEB-417 / WEB-726).
 */
export function NearbyRecommendationsCard(
  props: NearbyRecommendationsCardProps,
): React.ReactElement | null {
  return <NearbyRecommendationsCardView {...props} />;
}
